// Tenant settings (BUILD_SPEC.md §5.1, §11 Settings) — over-receipt tolerance %,
// invoice footer, other per-tenant defaults. GET is open to any authenticated
// role; PATCH is ADMIN only (services/api/src/modules/config/tenant.ts).
import { useEffect, useState } from 'react';
import { Alert, Button, Loader, NumberInput, Paper, Stack, Switch, Text, Textarea, Title } from '@mantine/core';
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { useAuth } from '../../../auth/AuthContext';
import { parseNum } from '../../../lib/parseNum';
import { useIsAdmin } from '../permissions';
import { useTenant, useUpdateTenantSettings } from '../api';

export function TenantSettingsPanel() {
  const isAdmin = useIsAdmin();
  const auth = useAuth();
  const tenantQuery = useTenant();
  const update = useUpdateTenantSettings();

  const [overReceiptTolerancePct, setOverReceiptTolerancePct] = useState<number | string>('');
  const [invoiceFooter, setInvoiceFooter] = useState('');
  // Per-tenant silo module gate (Stuart ruling 2026-08-26, verbatim: "Silos as
  // a form of warehouse should be able to be configured on or off, with the
  // default being off because we don't have any silos for Howick."). ABSENT
  // (undefined) reads as off, same as everywhere else this flag is read
  // (lib/tenantFeatures.ts).
  const [silosEnabled, setSilosEnabled] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!tenantQuery.data) return;
    const settings = tenantQuery.data.settings;
    setOverReceiptTolerancePct(settings.overReceiptTolerancePct ?? 5);
    setInvoiceFooter(settings.invoiceFooter ?? '');
    setSilosEnabled(settings.silosEnabled === true);
  }, [tenantQuery.data]);

  function handleSave() {
    setSaved(false);
    const parsedOverReceiptTolerancePct = parseNum(overReceiptTolerancePct);
    update.mutate(
      {
        overReceiptTolerancePct: parsedOverReceiptTolerancePct === null ? undefined : parsedOverReceiptTolerancePct,
        invoiceFooter: invoiceFooter.trim() === '' ? null : invoiceFooter,
        silosEnabled,
      },
      {
        onSuccess: () => {
          setSaved(true);
          // The PATCH only updates the DB row — AuthContext's in-memory
          // `activeTenant.settings` (the thing useTenantFeatures, nav, the
          // silo-counts route guards, the Home task filter and the dashboard/
          // reports gating all actually read) is a snapshot taken at sign-in
          // and otherwise never refreshed. Without this, an ADMIN who flips
          // "Silo tracking" ON sees no change anywhere until they re-login or
          // round-trip the tenant chooser (gap found 2026-08-26). Re-fetching
          // /me here closes that: refreshMemberships replaces `tenants`
          // wholesale with the server's current view, same as it does on
          // sign-in — fire-and-forget, this screen doesn't need to await it,
          // and the call is a safe no-op if a refresh already happens to be
          // in flight (refreshInFlightRef, AuthContext.tsx) rather than a
          // stale write, since nothing here depends on ITS completion.
          void auth.refreshMemberships();
        },
      },
    );
  }

  if (tenantQuery.isLoading) {
    return <Loader size="sm" />;
  }

  if (tenantQuery.isError) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={16} />}>
        Failed to load tenant settings.
      </Alert>
    );
  }

  return (
    <Stack gap="lg" maw={560}>
      <div>
        <Title order={2}>Tenant settings</Title>
        <Text c="dimmed" size="sm">
          {tenantQuery.data?.code} — {tenantQuery.data?.name}
        </Text>
      </div>

      {!isAdmin && (
        <Alert color="gray" variant="light">
          Read-only — ADMIN role is required to change tenant settings.
        </Alert>
      )}

      <Paper withBorder radius="md" p="lg">
        <Stack gap="md">
          <NumberInput
            label="Over-receipt tolerance (%)"
            description="Receipts exceeding a PO line's ordered quantity by more than this are blocked at posting — the operator must reduce the quantity or raise the tolerance first. Default 5%."
            min={0}
            max={100}
            decimalScale={2}
            value={overReceiptTolerancePct}
            onChange={setOverReceiptTolerancePct}
            disabled={!isAdmin}
          />
          <Textarea
            label="Invoice footer"
            description="Printed on every generated invoice/credit note PDF."
            autosize
            minRows={2}
            maxRows={6}
            value={invoiceFooter}
            onChange={(e) => setInvoiceFooter(e.currentTarget.value)}
            disabled={!isAdmin}
          />
          <Switch
            label="Silo tracking"
            description="Turns on the silo screens (readings, calibration, drift) for plants that store product in silos. Leave off if this site has none."
            checked={silosEnabled}
            onChange={(e) => setSilosEnabled(e.currentTarget.checked)}
            disabled={!isAdmin}
          />

          {update.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {update.error instanceof ApiRequestError ? update.error.message : 'Failed to save tenant settings.'}
            </Alert>
          )}
          {saved && !update.isPending && (
            <Alert color="green" icon={<IconCircleCheck size={16} />}>
              Saved.
            </Alert>
          )}

          {isAdmin && (
            <Button onClick={handleSave} loading={update.isPending} style={{ alignSelf: 'flex-start' }}>
              Save settings
            </Button>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
